import { useMemo } from 'react';
import { DropdownItemProps } from 'semantic-ui-react';
import orderBy from 'lodash/orderBy';
import deburr from 'lodash/deburr';
import { HomelessnessSummary } from '../types';
import { HomelessnessesApi } from '../api';
import { ApiError, defaultErrorHandler } from 'api';
import { useQuery } from 'react-query';

export interface HomelessnessLists {
  homelessnesses: HomelessnessSummary[];
  dropdownItems: DropdownItemProps[];
  indexedHomelessnesses: Record<number, HomelessnessSummary>;
  isFetched: boolean;
  isFetching: boolean;
}

export const useFetchHomelessnesses = (options?: {
  staleTime?: number;
}): HomelessnessLists => {
  const {
    data: homelessnesses,
    isFetching,
    isFetched,
  } = useQuery('homelessnesses', HomelessnessesApi.getAll, {
    staleTime: 15 * 60 * 1000,
    onSettled: (_, error) =>
      error && error instanceof ApiError && defaultErrorHandler(error),
    ...options,
  });

  const indexedHomelessnesses = useMemo(
    () =>
      (homelessnesses || []).reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {} as Record<number, HomelessnessSummary>),
    [homelessnesses]
  );

  const dropdownItems = useMemo(() => {
    const activeHomelessnesses = (homelessnesses || []).filter(
      (homelessness) => homelessness.isActive
    );

    const items = activeHomelessnesses.map((homelessness) => ({
      key: homelessness.id,
      value: homelessness.id,
      text: homelessness.name,
    }));

    const sortedItems = orderBy(items, (item) =>
      deburr(item.text).toLowerCase()
    );

    return sortedItems;
  }, [homelessnesses]);

  return {
    homelessnesses: homelessnesses || [],
    dropdownItems,
    indexedHomelessnesses,
    isFetched,
    isFetching,
  };
};
